import {
  BaseQueryFn,
  createApi,
  FetchArgs,
  fetchBaseQuery,
  FetchBaseQueryError,
} from "@reduxjs/toolkit/query/react";
import { Devotion, LiveStreamSettings, Sermon, SermonSeries } from "@/redux/types";
import { WEB_AUTH_PROVIDERS } from "@/config/authProviders";
import { API_BASE_URL } from "@/config/apiBase";

interface AnalyticsData {
  newUsers: number;
  totalUsers: number;
  newCourses: number;
  totalCourses: number;
  accountsReached: number;
  usersLeft: number;
  dailyActiveUsers: number;
  weeklyActiveUsers: number;
  averageSessionTime: number;
  courseCompletionRate: number;
  userEngagementRate: number;
  totalDevotions: number;
  newDevotions: number;
  latestDevotionEngagement?: {
    devotion: {
      id: string;
      title: string;
      month: string;
      day: string;
      year: number | null;
    };
    likesCount: number;
    commentsCount: number;
    likeEvents: Array<{
      userName: string;
      email: string;
    }>;
    commentEvents: Array<{
      userName: string;
      email: string;
      text: string;
      createdAt: string;
    }>;
  } | null;
}

interface PerformanceData {
  topUsers: Array<{
    name: string;
    score: number;
    courses: number;
    rank: number;
  }>;
  topCourses: Array<{
    title: string;
    completion: number;
    students: number;
    rank: number;
  }>;
  weeklyActivity: Array<{
    date: string;
    activeUsers: number;
    percentage: number;
  }>;
  systemMetrics: {
    apiUptime: number;
    databaseHealth: number;
    averageResponseTime: number;
    totalRequests: number;
  };
}

interface AuthProvidersResponse {
  google: {
    webClientId: string;
  };
}

interface SocialAuthBody {
  idToken?: string;
  token?: string;
}

const getStoredUser = () => {
  try {
    const raw = localStorage.getItem("user");
    return raw ? JSON.parse(raw) : null;
  } catch (_) {
    return null;
  }
};

const buildQueryString = (params?: Record<string, unknown>) => {
  if (!params) {
    return "";
  }

  const query = new URLSearchParams();
  Object.entries(params).forEach(([key, value]) => {
    if (typeof value !== "undefined" && value !== null && String(value) !== "") {
      query.set(key, String(value));
    }
  });

  const serialized = query.toString();
  return serialized ? `?${serialized}` : "";
};

const rawBaseQuery = fetchBaseQuery({
  baseUrl: API_BASE_URL,
  prepareHeaders: (headers) => {
    const token = getStoredUser()?.token || "";
    if (token) {
      headers.set("Authorization", `Bearer ${token}`);
    }
    return headers;
  },
});

const baseQueryWithReauth: BaseQueryFn<
  string | FetchArgs,
  unknown,
  FetchBaseQueryError
> = async (args, api, extraOptions) => {
  let result = await rawBaseQuery(args, api, extraOptions);

  const url = typeof args === "string" ? args : args.url;
  if (result.error?.status === 401 && url !== "/users/refresh-token") {
    const storedUser = getStoredUser();
    const refreshToken = storedUser?.refreshToken;

    if (refreshToken) {
      const refreshResult = await rawBaseQuery(
        {
          url: "/users/refresh-token",
          method: "POST",
          body: { refreshToken },
        },
        api,
        extraOptions
      );

      if (refreshResult.data) {
        localStorage.setItem("user", JSON.stringify(refreshResult.data));
        localStorage.setItem("token", (refreshResult.data as any).token || "");
        result = await rawBaseQuery(args, api, extraOptions);
      } else {
        localStorage.removeItem("user");
        localStorage.removeItem("token");
      }
    } else {
      localStorage.removeItem("user");
      localStorage.removeItem("token");
    }
  }

  return result;
};

export const apiSlice = createApi({
  reducerPath: "api",
  baseQuery: baseQueryWithReauth,
  tagTypes: [
    "Devotions",
    "DevotionComments",
    "Sermons",
    "LiveStream",
    "Bibles",
    "BiblePersonal",
  ],
  endpoints: (builder) => ({
    login: builder.mutation({
      query: (credentials) => ({
        url: "/users/login",
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify(credentials),
      }),
    }),
    getAuthProviders: builder.query<AuthProvidersResponse, void>({
      queryFn: async () => ({ data: WEB_AUTH_PROVIDERS }),
    }),
    socialAuth: builder.mutation({
      query: (body: SocialAuthBody) => ({
        url: "/users/auth/google/verify",
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: {
          token: body?.idToken || body?.token,
        },
      }),
    }),
    forgotPassword: builder.mutation({
      query: (email) => ({
        url: "/users/forgot-password",
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({ email }),
      }),
    }),
    resetPassword: builder.mutation({
      query: ({ token, newPassword }) => ({
        url: `/users/reset-password/${token}`,
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({ password: newPassword }),
      }),
    }),
    sendMessage: builder.mutation({
      query: ({ firstName, lastName, email, message }) => ({
        url: "/users/contact",
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({ firstName, lastName, email, message }),
      }),
    }),
    getContacts: builder.query<Array<{ _id: string; firstName: string; lastName: string; email: string; message: string; createdAt: string; repliedAt?: string }>, void>({
      query: () => "/users/contacts",
    }),
    sendContactReply: builder.mutation<{ message: string }, { id: string; subject: string; message: string }>({
      query: ({ id, subject, message }) => ({
        url: `/users/contacts/${id}/reply`,
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({ subject, message }),
      }),
    }),
    // Explore (Supplements)
    getExploreCategories: builder.query<any[], void>({
      query: () => "/explore/categories",
    }),
    getExploreItems: builder.query<any[], { categoryId: string }>({
      query: ({ categoryId }) =>
        `/explore/items?categoryId=${encodeURIComponent(categoryId)}`,
    }),
    getExploreItem: builder.query<any, { id: string }>({
      query: ({ id }) => `/explore/items/${id}`,
    }),
    // Explore (Admin)
    adminGetExploreCategories: builder.query<any[], void>({
      query: () => "/explore/admin/categories",
    }),
    adminCreateExploreCategory: builder.mutation<
      any,
      { title: string; description?: string; order?: number; isPublished?: boolean }
    >({
      query: (body) => ({
        url: "/explore/admin/categories",
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify(body),
      }),
    }),
    adminUpdateExploreCategory: builder.mutation<
      any,
      { id: string; title?: string; description?: string; order?: number; isPublished?: boolean }
    >({
      query: ({ id, ...body }) => ({
        url: `/explore/admin/categories/${id}`,
        method: "PUT",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify(body),
      }),
    }),
    adminDeleteExploreCategory: builder.mutation<{ message: string }, { id: string }>({
      query: ({ id }) => ({
        url: `/explore/admin/categories/${id}`,
        method: "DELETE",
      }),
    }),
    adminGetExploreItems: builder.query<any[], { categoryId?: string } | void>({
      query: (arg) => {
        const categoryId = arg && "categoryId" in arg ? arg.categoryId : undefined;
        return categoryId
          ? `/explore/admin/items?categoryId=${encodeURIComponent(categoryId)}`
          : "/explore/admin/items";
      },
    }),
    adminCreateExploreItem: builder.mutation<any, { categoryId: string; formData: FormData }>({
      query: ({ categoryId, formData }) => {
        formData.append("categoryId", categoryId);
        return {
          url: "/explore/admin/items",
          method: "POST",
          body: formData,
        };
      },
    }),
    adminUpdateExploreItem: builder.mutation<
      any,
      { id: string; title?: string; description?: string; order?: number; isPublished?: boolean; formData?: FormData }
    >({
      query: ({ id, formData, ...body }) => {
        if (formData) {
          // Append text fields to formData if they exist
          if (body.title !== undefined) formData.append("title", body.title);
          if (body.description !== undefined) formData.append("description", body.description);
          if (body.order !== undefined) formData.append("order", String(body.order));
          if (body.isPublished !== undefined) formData.append("isPublished", String(body.isPublished));
          return {
            url: `/explore/admin/items/${id}`,
            method: "PUT",
            body: formData,
          };
        }
        return {
          url: `/explore/admin/items/${id}`,
          method: "PUT",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify(body),
        };
      },
    }),
    adminDeleteExploreItem: builder.mutation<{ message: string }, { id: string }>({
      query: ({ id }) => ({
        url: `/explore/admin/items/${id}`,
        method: "DELETE",
      }),
    }),
    signup: builder.mutation({
      query: ({ firstName, lastName, email, password }) => ({
        url: "/users/signup",
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({ firstName, lastName, email, password }),
      }),
    }),
    createUser: builder.mutation({
      query: (userData) => {
        const formData = new FormData();
        formData.append("firstName", userData.firstName);
        formData.append("lastName", userData.lastName);
        formData.append("email", userData.email);
        formData.append("password", userData.password);
        formData.append("role", userData.role);
        if (userData.avatar) {
          formData.append("avatar", userData.avatar);
        }

        return {
          url: "/users/signup",
          method: "POST",
          body: formData,
        };
      },
    }),
    getUsers: builder.query({
      query: () => "/users",
      providesTags: ["Devotions"],
    }),
    getDeletedUsersCount: builder.query<{ deletedUsersCount: number }, void>({
      query: () => "/users/deleted-count",
    }),
    updateUser: builder.mutation({
      query: ({ id, updatedUser }) => ({
        url: `/users/profile/${id}`,
        method: "PUT",
        headers: {
          // Don't set the "Content-Type" header, as it will be set automatically by the browser
        },
        body: updatedUser,
      }),
    }),
    getUserById: builder.query({
      query: (id) => `/users/get/${id}`,
    }),
    getCurrentUser: builder.query({
      query: () => "/users/current",
    }),
    deleteUser: builder.mutation({
      query: (userId) => ({
        url: `/users/${userId}`,
        method: "DELETE",
      }),
    }),
    deactivateUser: builder.mutation({
      query: ({ userId, status }) => ({
        url: `/users/status/${userId}`,
        method: "PUT",
        body: { status },
      }),
    }),
    activateUser: builder.mutation({
      query: ({ userId, status }) => ({
        url: `/users/status/${userId}`,
        method: "PUT",
        body: { status },
      }),
    }),
    getCourses: builder.query({
      query: () => "/course/getall",
    }),
    getCourseById: builder.query({
      query: (id) => `/course/get/${id}`,
    }),
    getDevotions: builder.query<Devotion[], void>({
      query: () => ({
        url: "/devotion/show?limit=100&sort=desc",
      }),
      providesTags: ["Devotions"],
    }),
    getDevotionById: builder.query<Devotion, string>({
      query: (id) => `/devotion/${id}`,
    }),
    createDevotion: builder.mutation<void, FormData>({
      query: (newDevotion) => {
        const formData = new FormData();
        Object.entries(newDevotion).forEach(([key, value]) => {
          formData.append(key, value);
        });
        return {
          url: "/devotion/create",
          method: "POST",
          headers: {
            "Content-Type": "multipart/form-data",
          },
          body: formData,
        };
      },
      invalidatesTags: [{ type: "Devotions", id: "LIST" }],
    }),
    updateDevotion: builder.mutation<void, { id: string; updatedDevotion: FormData }>({
      query: ({ id, updatedDevotion }) => {
        const formData = new FormData();
        Object.entries(updatedDevotion).forEach(([key, value]) => {
          formData.append(key, value);
        });

        return {
          url: `/devotion/${id}`,
          method: "PUT",
          body: formData,
        };
      },
    }),
    deleteDevotion: builder.mutation({
      query: (id) => ({
        url: `/devotion/${id}`,
        method: "DELETE",
      }),
      invalidatesTags: ["Devotions"],
    }),
    getAvailableYears: builder.query<number[], void>({
      query: () => "/devotion/years",
    }),
    getDevotionsByYear: builder.query<Devotion[], number>({
      query: (year) => `/devotion/year/${year}`,
      providesTags: ["Devotions"],
    }),
    getMonthsByYear: builder.query<string[], number>({
      query: (year) => `/devotion/year/${year}/months`,
      providesTags: ["Devotions"],
    }),
    getDevotionsByYearAndMonth: builder.query<Devotion[], { year: number; month: string }>({
      query: ({ year, month }) => `/devotion/year/${year}/month/${encodeURIComponent(month)}`,
      providesTags: ["Devotions"],
    }),
    batchUpdateDevotionYears: builder.mutation<
      { message: string; results: any[]; errors?: any[] },
      { updates: Array<{ id: string; year: number }> }
    >({
      query: (body) => ({
        url: "/devotion/batch-update-years",
        method: "POST",
        body,
      }),
      invalidatesTags: ["Devotions"],
    }),
    createDevotionsForNewYear: builder.mutation<{ message: string }, { sourceYear: number; targetYear: number }>({
      query: ({ sourceYear, targetYear }) => ({
        url: "/devotion/copy-year",
        method: "POST",
        body: { sourceYear, targetYear },
      }),
      invalidatesTags: ["Devotions"],
    }),
    // Devotion likes and comments endpoints
    toggleLikeDevotion: builder.mutation<
      { message: string; likesCount: number; isLiked: boolean },
      string
    >({
      query: (id) => ({
        url: `/devotion/${id}/like`,
        method: "POST",
      }),
      invalidatesTags: ["Devotions"],
    }),
    getDevotionLikes: builder.query<
      { likesCount: number; isLiked: boolean },
      string
    >({
      query: (id) => `/devotion/${id}/likes`,
      providesTags: (_result, _error, id) => [{ type: "Devotions", id }],
    }),
    addDevotionComment: builder.mutation<
      { message: string; comment: any },
      { id: string; text: string }
    >({
      query: ({ id, text }) => ({
        url: `/devotion/${id}/comments`,
        method: "POST",
        body: { text },
      }),
      invalidatesTags: (_result, _error, { id }) => [
        { type: "Devotions", id },
        { type: "DevotionComments", id },
      ],
    }),
    getDevotionComments: builder.query<
      { comments: any[]; count: number },
      string
    >({
      query: (id) => `/devotion/${id}/comments`,
      providesTags: (_result, _error, id) => [
        { type: "DevotionComments", id },
      ],
    }),
    deleteDevotionComment: builder.mutation<
      { message: string },
      { id: string; commentId: string }
    >({
      query: ({ id, commentId }) => ({
        url: `/devotion/${id}/comments/${commentId}`,
        method: "DELETE",
      }),
      invalidatesTags: (_result, _error, { id }) => [
        { type: "Devotions", id },
        { type: "DevotionComments", id },
      ],
    }),
    // Share tracking endpoints
    trackDevotionShare: builder.mutation<
      { message: string; sharesCount: number },
      string
    >({
      query: (id) => ({
        url: `/devotion/${id}/share`,
        method: "POST",
      }),
      invalidatesTags: ["Devotions"],
    }),
    getDevotionShares: builder.query<
      { sharesCount: number },
      string
    >({
      query: (id) => `/devotion/${id}/shares`,
      providesTags: (_result, _error, id) => [{ type: "Devotions", id }],
    }),
    getAnalytics: builder.query<AnalyticsData, void>({
      query: () => "/analytics",
    }),
    getPerformanceAnalytics: builder.query<PerformanceData, void>({
      query: () => "/analytics/performance",
    }),
    getLiveStreamSettings: builder.query<LiveStreamSettings, void>({
      query: () => "/live-stream",
      providesTags: ["LiveStream"],
    }),
    updateLiveStreamSettings: builder.mutation<
      LiveStreamSettings,
      Partial<LiveStreamSettings>
    >({
      query: (body) => ({
        url: "/live-stream",
        method: "PUT",
        body,
      }),
      invalidatesTags: ["LiveStream"],
    }),
    // Devotion Plan endpoints
    getDevotionPlans: builder.query<{ items: any[]; total: number }, void | {}>({
      query: () => "/devotionPlan",
    }),
    adminGetDevotionPlans: builder.query<{ items: any[]; total: number }, void>({
      query: () => "/devotionPlan/admin/all",
    }),
    getDevotionPlanById: builder.query<any, string>({
      query: (id) => `/devotionPlan/${id}`,
    }),
    getMyDevotionPlans: builder.query<any[], { status?: string } | void>({
      query: (params) => {
        const queryParams = params?.status ? `?status=${params.status}` : "";
        return `/devotionPlan/user${queryParams}`;
      },
    }),
    startDevotionPlan: builder.mutation<{ message: string; userPlan: any }, string>({
      query: (id) => ({
        url: `/devotionPlan/${id}/start`,
        method: "POST",
      }),
    }),
    getDevotionPlanProgress: builder.query<any, string>({
      query: (id) => `/devotionPlan/${id}/progress`,
    }),
    updateDevotionPlanProgress: builder.mutation<
      { message: string; userPlan: any },
      { id: string; devotionId: string; completed: boolean }
    >({
      query: ({ id, devotionId, completed }) => ({
        url: `/devotionPlan/${id}/progress`,
        method: "PUT",
        body: { devotionId, completed },
      }),
    }),
    completeDevotionPlan: builder.mutation<{ message: string; userPlan: any }, string>({
      query: (id) => ({
        url: `/devotionPlan/${id}/complete`,
        method: "POST",
      }),
    }),
    restartDevotionPlan: builder.mutation<{ message: string; userPlan: any }, string>({
      query: (id) => ({
        url: `/devotionPlan/${id}/restart`,
        method: "POST",
      }),
    }),
    createDevotionPlan: builder.mutation<any, FormData>({
      query: (formData) => ({
        url: "/devotionPlan",
        method: "POST",
        body: formData,
      }),
    }),
    updateDevotionPlan: builder.mutation<any, { id: string; formData: FormData }>({
      query: ({ id, formData }) => ({
        url: `/devotionPlan/${id}`,
        method: "PUT",
        body: formData,
      }),
    }),
    deleteDevotionPlan: builder.mutation<{ message: string }, string>({
      query: (id) => ({
        url: `/devotionPlan/${id}`,
        method: "DELETE",
      }),
    }),
    getPlanDevotions: builder.query<{ items: any[]; total: number }, { id: string }>({
      query: ({ id }) => `/devotionPlan/${id}/devotions?sort=asc`,
    }),
    createPlanDevotion: builder.mutation<any, { id: string; formData: FormData }>({
      query: ({ id, formData }) => ({
        url: `/devotionPlan/${id}/devotions`,
        method: "POST",
        body: formData,
      }),
    }),
    updatePlanDevotion: builder.mutation<
      any,
      { id: string; devotionId: string; formData: FormData }
    >({
      query: ({ id, devotionId, formData }) => ({
        url: `/devotionPlan/${id}/devotions/${devotionId}`,
        method: "PUT",
        body: formData,
      }),
    }),
    deletePlanDevotion: builder.mutation<
      { message: string },
      { id: string; devotionId: string }
    >({
      query: ({ id, devotionId }) => ({
        url: `/devotionPlan/${id}/devotions/${devotionId}`,
        method: "DELETE",
      }),
    }),
    reorderPlanDevotion: builder.mutation<
      { message: string },
      { id: string; devotionId: string; direction: "up" | "down" }
    >({
      query: ({ id, devotionId, direction }) => ({
        url: `/devotionPlan/${id}/devotions/${devotionId}/reorder`,
        method: "POST",
        body: { direction },
      }),
    }),
    getSermons: builder.query<
      Sermon[],
      { includeDrafts?: boolean; mediaType?: "video" | "audio"; seriesRef?: string } | void
    >({
      query: (params) =>
        `/sermons${buildQueryString(params || undefined)}`,
      providesTags: ["Sermons"],
    }),
    getSermonSeries: builder.query<
      SermonSeries[],
      { includeDrafts?: boolean; mediaType?: "video" | "audio" } | void
    >({
      query: (params) =>
        `/sermons/series${buildQueryString(params || undefined)}`,
      providesTags: ["Sermons"],
    }),
    createSermonSeries: builder.mutation<
      SermonSeries,
      { title: string; description?: string; mediaType: "video" | "audio"; isPublished?: boolean }
    >({
      query: (body) => ({
        url: "/sermons/series",
        method: "POST",
        body,
      }),
      invalidatesTags: ["Sermons"],
    }),
    updateSermonSeries: builder.mutation<
      SermonSeries,
      { id: string; title: string; description?: string; isPublished?: boolean }
    >({
      query: ({ id, ...body }) => ({
        url: `/sermons/series/${id}`,
        method: "PUT",
        body,
      }),
      invalidatesTags: ["Sermons"],
    }),
    deleteSermonSeries: builder.mutation<{ message: string }, string>({
      query: (id) => ({
        url: `/sermons/series/${id}`,
        method: "DELETE",
      }),
      invalidatesTags: ["Sermons"],
    }),
    createSermon: builder.mutation<Sermon, FormData>({
      query: (formData) => ({
        url: "/sermons",
        method: "POST",
        body: formData,
      }),
      invalidatesTags: ["Sermons"],
    }),
    updateSermon: builder.mutation<Sermon, { id: string; formData: FormData }>({
      query: ({ id, formData }) => ({
        url: `/sermons/${id}`,
        method: "PUT",
        body: formData,
      }),
      invalidatesTags: ["Sermons"],
    }),
    deleteSermon: builder.mutation<{ message: string }, string>({
      query: (id) => ({
        url: `/sermons/${id}`,
        method: "DELETE",
      }),
      invalidatesTags: ["Sermons"],
    }),
    reorderSermon: builder.mutation<
      { message: string },
      { id: string; direction: "up" | "down" }
    >({
      query: ({ id, direction }) => ({
        url: `/sermons/${id}/reorder`,
        method: "POST",
        body: { direction },
      }),
      invalidatesTags: ["Sermons"],
    }),
    getAdminBibles: builder.query<
      any,
      {
        page?: number;
        limit?: number;
        search?: string;
        language?: string;
        status?: string;
        publicationStatus?: string;
        sortBy?: string;
        sortDir?: "asc" | "desc";
      } | void
    >({
      query: (params) => `/api/admin/bibles${buildQueryString(params || undefined)}`,
      providesTags: ["Bibles"],
    }),
    uploadBibles: builder.mutation<any, FormData>({
      query: (formData) => ({
        url: "/api/admin/bibles/upload",
        method: "POST",
        body: formData,
      }),
      invalidatesTags: ["Bibles"],
    }),
    updateBible: builder.mutation<any, { id: string; body: Record<string, unknown> }>({
      query: ({ id, body }) => ({
        url: `/api/admin/bibles/${id}`,
        method: "PATCH",
        body,
      }),
      invalidatesTags: ["Bibles"],
    }),
    importBible: builder.mutation<any, string>({
      query: (id) => ({
        url: `/api/admin/bibles/${id}/import`,
        method: "POST",
      }),
      invalidatesTags: ["Bibles"],
    }),
    reprocessBible: builder.mutation<any, string>({
      query: (id) => ({
        url: `/api/admin/bibles/${id}/reprocess`,
        method: "POST",
      }),
      invalidatesTags: ["Bibles"],
    }),
    getBibleImportReport: builder.query<any, string>({
      query: (id) => `/api/admin/bibles/${id}/import-report`,
      providesTags: ["Bibles"],
    }),
    getBiblePreview: builder.query<any, { id: string; bookId?: string; chapter?: number } | null>({
      query: (params) =>
        params
          ? `/api/admin/bibles/${params.id}/preview${buildQueryString({
              bookId: params.bookId,
              chapter: params.chapter,
            })}`
          : "/api/admin/bibles",
      providesTags: ["Bibles"],
    }),
    replaceBible: builder.mutation<any, { id: string; formData: FormData }>({
      query: ({ id, formData }) => ({
        url: `/api/admin/bibles/${id}/replace`,
        method: "POST",
        body: formData,
      }),
      invalidatesTags: ["Bibles"],
    }),
    publishBible: builder.mutation<any, string>({
      query: (id) => ({
        url: `/api/admin/bibles/${id}/publish`,
        method: "POST",
      }),
      invalidatesTags: ["Bibles"],
    }),
    unpublishBible: builder.mutation<any, string>({
      query: (id) => ({
        url: `/api/admin/bibles/${id}/unpublish`,
        method: "POST",
      }),
      invalidatesTags: ["Bibles"],
    }),
    deleteBible: builder.mutation<any, { id: string; confirm?: string }>({
      query: ({ id, confirm }) => ({
        url: `/api/admin/bibles/${id}`,
        method: "DELETE",
        body: { confirm },
      }),
      invalidatesTags: ["Bibles"],
    }),
    getPublicBibles: builder.query<any[], void>({
      query: () => "/api/bibles",
      providesTags: ["Bibles"],
    }),
    getPublicBible: builder.query<any, string>({
      query: (id) => `/api/bibles/${id}`,
      providesTags: ["Bibles"],
    }),
    getPublicBibleBooks: builder.query<any[], string>({
      query: (id) => `/api/bibles/${id}/books`,
      providesTags: ["Bibles"],
    }),
    getPublicBibleChapters: builder.query<number[], { id: string; bookId: string }>({
      query: ({ id, bookId }) => `/api/bibles/${id}/books/${bookId}/chapters`,
      providesTags: ["Bibles"],
    }),
    getPublicBibleChapter: builder.query<
      { verses: any[] },
      { id: string; bookId: string; chapterNumber: number }
    >({
      query: ({ id, bookId, chapterNumber }) =>
        `/api/bibles/${id}/books/${bookId}/chapters/${chapterNumber}`,
      providesTags: ["Bibles"],
    }),
    searchPublicBible: builder.query<
      any,
      { id: string; q: string; page?: number; limit?: number; bookId?: string }
    >({
      query: ({ id, ...params }) =>
        `/api/bibles/${id}/search${buildQueryString(params)}`,
      providesTags: ["Bibles"],
    }),
    getMyBibleHighlights: builder.query<any, { translationId?: string; bookId?: string; color?: string } | void>({
      query: (params) => `/api/bible/me/highlights${buildQueryString(params || undefined)}`,
      providesTags: ["BiblePersonal"],
    }),
    saveMyBibleHighlight: builder.mutation<any, Record<string, unknown>>({
      query: (body) => ({
        url: "/api/bible/me/highlights",
        method: "POST",
        body,
      }),
      invalidatesTags: ["BiblePersonal"],
    }),
    updateMyBibleHighlight: builder.mutation<any, { id: string; color: string }>({
      query: ({ id, color }) => ({
        url: `/api/bible/me/highlights/${id}`,
        method: "PATCH",
        body: { color },
      }),
      invalidatesTags: ["BiblePersonal"],
    }),
    deleteMyBibleHighlight: builder.mutation<any, string>({
      query: (id) => ({
        url: `/api/bible/me/highlights/${id}`,
        method: "DELETE",
      }),
      invalidatesTags: ["BiblePersonal"],
    }),
    getMyBibleNotes: builder.query<any, { translationId?: string; bookId?: string; q?: string } | void>({
      query: (params) => `/api/bible/me/notes${buildQueryString(params || undefined)}`,
      providesTags: ["BiblePersonal"],
    }),
    createMyBibleNote: builder.mutation<any, Record<string, unknown>>({
      query: (body) => ({
        url: "/api/bible/me/notes",
        method: "POST",
        body,
      }),
      invalidatesTags: ["BiblePersonal"],
    }),
    updateMyBibleNote: builder.mutation<any, { id: string; text: string }>({
      query: ({ id, text }) => ({
        url: `/api/bible/me/notes/${id}`,
        method: "PATCH",
        body: { text },
      }),
      invalidatesTags: ["BiblePersonal"],
    }),
    deleteMyBibleNote: builder.mutation<any, string>({
      query: (id) => ({
        url: `/api/bible/me/notes/${id}`,
        method: "DELETE",
      }),
      invalidatesTags: ["BiblePersonal"],
    }),
    getMyBibleBookmarks: builder.query<any, { translationId?: string; bookId?: string } | void>({
      query: (params) => `/api/bible/me/bookmarks${buildQueryString(params || undefined)}`,
      providesTags: ["BiblePersonal"],
    }),
    saveMyBibleBookmark: builder.mutation<any, Record<string, unknown>>({
      query: (body) => ({
        url: "/api/bible/me/bookmarks",
        method: "POST",
        body,
      }),
      invalidatesTags: ["BiblePersonal"],
    }),
    deleteMyBibleBookmark: builder.mutation<any, string>({
      query: (id) => ({
        url: `/api/bible/me/bookmarks/${id}`,
        method: "DELETE",
      }),
      invalidatesTags: ["BiblePersonal"],
    }),
    getMyBibleProgress: builder.query<any, { translationId?: string } | void>({
      query: (params) => `/api/bible/me/progress${buildQueryString(params || undefined)}`,
      providesTags: ["BiblePersonal"],
    }),
    saveMyBibleProgress: builder.mutation<any, Record<string, unknown>>({
      query: (body) => ({
        url: "/api/bible/me/progress",
        method: "PUT",
        body,
      }),
      invalidatesTags: ["BiblePersonal"],
    }),
  }),
});

export const {
  useSignupMutation,
  useCreateUserMutation,
  useGetUsersQuery,
  useLazyGetUsersQuery,
  useLoginMutation,
  useGetAuthProvidersQuery,
  useSocialAuthMutation,
  useForgotPasswordMutation,
  useResetPasswordMutation,
  useUpdateUserMutation,
  useGetUserByIdQuery,
  useDeleteUserMutation,
  useDeactivateUserMutation,
  useActivateUserMutation,
  useGetCoursesQuery,
  useGetCourseByIdQuery,
  useGetDevotionsQuery,
  useGetDevotionByIdQuery,
  useCreateDevotionMutation,
  useUpdateDevotionMutation,
  useDeleteDevotionMutation,
  useGetAvailableYearsQuery,
  useGetDevotionsByYearQuery,
  useGetMonthsByYearQuery,
  useGetDevotionsByYearAndMonthQuery,
  useLazyGetDevotionsByYearAndMonthQuery,
  useBatchUpdateDevotionYearsMutation,
  useCreateDevotionsForNewYearMutation,
  useToggleLikeDevotionMutation,
  useGetDevotionLikesQuery,
  useAddDevotionCommentMutation,
  useGetDevotionCommentsQuery,
  useDeleteDevotionCommentMutation,
  useTrackDevotionShareMutation,
  useGetDevotionSharesQuery,
  useGetCurrentUserQuery,
  useSendMessageMutation,
  useGetAnalyticsQuery,
  useGetPerformanceAnalyticsQuery,
  useGetLiveStreamSettingsQuery,
  useUpdateLiveStreamSettingsMutation,
  useGetDeletedUsersCountQuery,
  useGetContactsQuery,
  useSendContactReplyMutation,
  useGetExploreCategoriesQuery,
  useGetExploreItemsQuery,
  useGetExploreItemQuery,
  useAdminGetExploreCategoriesQuery,
  useAdminCreateExploreCategoryMutation,
  useAdminUpdateExploreCategoryMutation,
  useAdminDeleteExploreCategoryMutation,
  useAdminGetExploreItemsQuery,
  useAdminCreateExploreItemMutation,
  useAdminUpdateExploreItemMutation,
  useAdminDeleteExploreItemMutation,
  useGetDevotionPlansQuery,
  useAdminGetDevotionPlansQuery,
  useGetDevotionPlanByIdQuery,
  useGetMyDevotionPlansQuery,
  useStartDevotionPlanMutation,
  useGetDevotionPlanProgressQuery,
  useUpdateDevotionPlanProgressMutation,
  useCompleteDevotionPlanMutation,
  useRestartDevotionPlanMutation,
  useCreateDevotionPlanMutation,
  useUpdateDevotionPlanMutation,
  useDeleteDevotionPlanMutation,
  useGetPlanDevotionsQuery,
  useCreatePlanDevotionMutation,
  useUpdatePlanDevotionMutation,
  useDeletePlanDevotionMutation,
  useReorderPlanDevotionMutation,
  useGetSermonsQuery,
  useGetSermonSeriesQuery,
  useCreateSermonSeriesMutation,
  useUpdateSermonSeriesMutation,
  useDeleteSermonSeriesMutation,
  useCreateSermonMutation,
  useUpdateSermonMutation,
  useDeleteSermonMutation,
  useReorderSermonMutation,
  useGetAdminBiblesQuery,
  useUploadBiblesMutation,
  useUpdateBibleMutation,
  useImportBibleMutation,
  useReprocessBibleMutation,
  useGetBibleImportReportQuery,
  useGetBiblePreviewQuery,
  useReplaceBibleMutation,
  usePublishBibleMutation,
  useUnpublishBibleMutation,
  useDeleteBibleMutation,
  useGetPublicBiblesQuery,
  useGetPublicBibleQuery,
  useGetPublicBibleBooksQuery,
  useGetPublicBibleChaptersQuery,
  useGetPublicBibleChapterQuery,
  useSearchPublicBibleQuery,
  useGetMyBibleHighlightsQuery,
  useSaveMyBibleHighlightMutation,
  useUpdateMyBibleHighlightMutation,
  useDeleteMyBibleHighlightMutation,
  useGetMyBibleNotesQuery,
  useCreateMyBibleNoteMutation,
  useUpdateMyBibleNoteMutation,
  useDeleteMyBibleNoteMutation,
  useGetMyBibleBookmarksQuery,
  useSaveMyBibleBookmarkMutation,
  useDeleteMyBibleBookmarkMutation,
  useGetMyBibleProgressQuery,
  useSaveMyBibleProgressMutation,
} = apiSlice;
